/**
 * =============================================================================
 *  CHU THICH NAO GHI DUOC RA TEP - VA CUA CHAN "DAU VO HINH"
 * =============================================================================
 *
 *  Tep nay la CUA KIEM DUY NHAT cho moi chu thich, va no duoc goi o HAI cho:
 *
 *      PdfCoreBrowser.apply()   - luc nguoi dung vua bam, de tu choi NGAY
 *      writeAnnotations()       - luc ghi that, de khong bao gio ghi ra mot dau
 *                                 ma cua kiem tren da tu choi
 *
 *  Hai cho dung CHUNG mot ham chu khong phai hai ban sao cua cung luat. Do la
 *  bai hoc da tra gia o duong sua chu: khi ban xem truoc va duong ghi tinh bang
 *  hai doan ma khac nhau, chung troi khoi nhau va ban xem truoc noi doi.
 *
 * =============================================================================
 *  CHE DO HONG SO 1 DOI VOI CHU THICH: DAU VO HINH
 * =============================================================================
 *  Mot chu thich mau gan trang, hoac do mo bang 0, tao ra mot tep PDF HOAN TOAN
 *  HOP LE. page.getAnnotations() doc ra no. Acrobat liet ke no trong bang chu
 *  thich. Nhung tren trang giay thi KHONG CO GI CA.
 *
 *  Do la dung che do hong "chu trang tren nen trang" cua duong sua chu, chi
 *  doi ao. Nen cua chan o day cung dung mot phep do va cung mot nguong:
 *  khoang cach L1 toi mau nen (trang) phai >= ANNOTATION_MIN_COLOR_DISTANCE.
 *
 *  To sang con them mot ly do rieng: no duoc ve o che do hoa tron Multiply.
 *  Multiply voi mau gan trang la phep nhan voi 1 - tuc la khong doi gi het.
 */
import type { Annotation, AnnotationKind, PdfErrorCode } from '@pdfeditor/types';
import { MAX_STROKE_WIDTH_PT, MIN_STROKE_WIDTH_PT, usesVectorRect } from './shapes';

/**
 * Nguong khoang cach mau toi nen trang. Cung con so voi COLOR_MIN_DISTANCE cua
 * duong sua chu, va co y de giong nhau: hai cho cung tra loi cung cau hoi
 * "nguoi dung co NHIN THAY thu ta vua ghi ra khong".
 */
export const ANNOTATION_MIN_COLOR_DISTANCE = 0.15;

/** Do mo duoi muc nay thi dau ve ra khong the nhin thay. */
export const ANNOTATION_MIN_OPACITY = 0.05;

/** Kich thuoc o bieu tuong cua ghi chu dinh, tinh bang diem. */
export const NOTE_ICON_SIZE_PT = 20;

/** Be rong net mac dinh cho ve tay / khung / duong thang. */
export const DEFAULT_BORDER_WIDTH_PT = 1.5;

/**
 * Cac loai chu thich duong ghi trinh duyet dung ra duoc DOI TUONG /Annot THAT.
 *
 * "That" o day co nghia cu the: mot muc trong /Annots cua trang, dung /Subtype
 * theo dac ta, kem mot dong hinh dang /AP. Trinh xem khac (Acrobat, Preview,
 * Foxit) doc no nhu chu thich sua duoc, chu khong phai nhu net ve chet dinh vao
 * noi dung trang.
 */
export const WRITABLE_KINDS: readonly AnnotationKind[] = [
  'underline',
  'strikeout',
  'squiggly',
  'ink',
  'square',
  'circle',
  'line',
  'arrow',
  'text-note',
  // 'free-text' = HOP CHU. Ghi duoc tu khi duong ghi nhung font dong goi (Noto
  // Sans, phu du dau tieng Viet) vao chinh dong hinh dang, thay vi dua vao muoi
  // bon font chuan ma hoa WinAnsi. Xem textBoxAppearance() trong appearance.ts.
  'free-text',
  // 'stamp' = ANH da nhung. Ghi duoc, NHUNG chi khi mang theo `imageRef` - xem
  // cua kiem rieng trong checkAnnotation(). Mot con dau khong co anh la mot o
  // trong, va o trong thi khong duoc ghi ra.
  'stamp',
];

/**
 * Loai CHUA ghi duoc, kem ly do ky thuat that.
 *
 * Day khong phai danh sach "chua lam kip" - muc con lai vuong mot vat can ma bo
 * cong cu hien tai khong vuot qua duoc ma khong noi doi voi nguoi dung.
 *
 * TO SANG DA BI GO BO, va no khong nam o day: no khong phai "chua lam duoc" ma
 * la mot chuc nang nguoi dung noi ro la khong can. Mot loai da go bo roi vao
 * nhanh "chưa hỗ trợ" chung o duoi, va do la cau tra loi dung.
 */
export const UNWRITABLE_REASON: Partial<Record<AnnotationKind, string>> = {
  // TRONG. Giu lai bang nay chu khong xoa: no la cho khai bao mot loai CHUA ghi
  // duoc kem ly do ky thuat that, va con cho do la thu ngan mot loai moi lang le
  // roi vao nhanh "chua ho tro" chung chung.
  //
  // 'free-text' TUNG nam o day voi ly do "muoi bon font chuan ma hoa WinAnsi,
  // khong co cho cho dau tieng Viet". Ly do do da het hieu luc: duong ghi nhung
  // font dong goi vao dong hinh dang, nen dau tieng Viet ra dung.
};

export type AnnotationRejection = {
  code: PdfErrorCode;
  /** Ly do doc duoc, tieng Viet, hien thang cho nguoi dung. */
  reason: string;
};

export type AnnotationCheck = { ok: true } | ({ ok: false } & AnnotationRejection);

const OK: AnnotationCheck = { ok: true };

function reject(code: PdfErrorCode, reason: string): AnnotationCheck {
  return { ok: false, code, reason };
}

function finite(...values: number[]): boolean {
  return values.every((v) => Number.isFinite(v));
}

/** Khoang cach L1 giua hai mau trong khong gian 0..1. Cung phep do voi duong sua chu. */
export function colorDistance(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): number {
  return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]);
}

const WHITE: readonly [number, number, number] = [1, 1, 1];

/**
 * Kiem tra mot chu thich TRUOC khi ghi nhan no.
 *
 * `pageCount` la so trang cua tai lieu dang mo; bo qua neu ben goi chua biet.
 */
export function checkAnnotation(annotation: Annotation, pageCount?: number): AnnotationCheck {
  const unwritable = UNWRITABLE_REASON[annotation.kind];
  if (unwritable) {
    return reject('unsupported-feature', unwritable);
  }
  if (!WRITABLE_KINDS.includes(annotation.kind)) {
    return reject('unsupported-feature', `Chưa hỗ trợ loại chú thích "${annotation.kind}".`);
  }

  if (!Number.isInteger(annotation.pageIndex) || annotation.pageIndex < 0) {
    return reject('page-out-of-range', `Trang ${annotation.pageIndex + 1} không hợp lệ.`);
  }
  if (pageCount !== undefined && annotation.pageIndex >= pageCount) {
    return reject(
      'page-out-of-range',
      `Trang ${annotation.pageIndex + 1} không tồn tại (tài liệu có ${pageCount} trang).`,
    );
  }

  const { x, y, width, height } = annotation.rect;
  if (!finite(x, y, width, height)) {
    return reject('unsupported-feature', 'Vùng của chú thích không hợp lệ.');
  }
  // Duong thang va mui ten dung `rect` lam VECTO, nen be rong / chieu cao AM la
  // hop le o day va CHI o day: no ma hoa huong ve. Xem shapes.ts.
  if (!usesVectorRect(annotation.kind) && (width < 0 || height < 0)) {
    return reject('unsupported-feature', 'Vùng của chú thích không hợp lệ.');
  }

  if (annotation.strokeWidthPt !== undefined) {
    const w = annotation.strokeWidthPt;
    if (!finite(w) || w < MIN_STROKE_WIDTH_PT || w > MAX_STROKE_WIDTH_PT) {
      return reject(
        'unsupported-feature',
        `Bề rộng nét phải nằm trong khoảng ${MIN_STROKE_WIDTH_PT}–${MAX_STROKE_WIDTH_PT} điểm.`,
      );
    }
  }

  // `null` la "khong tô" va hoan toan hop le - chi gia tri KHAC null moi phai
  // la mot mau dung. Xem ghi chu `fillRgb` trong hop dong kieu.
  if (annotation.fillRgb !== undefined && annotation.fillRgb !== null) {
    const fill = annotation.fillRgb;
    if (fill.length !== 3 || !fill.every((c) => Number.isFinite(c) && c >= 0 && c <= 1)) {
      return reject('unsupported-feature', 'Màu tô của hình phải nằm trong khoảng 0–1 mỗi kênh.');
    }
  }

  if (annotation.kind === 'stamp') {
    // Anh chen vao phai co ANH. Mot /Stamp khong co tham chieu anh se ghi ra
    // mot o rong - dung che do hong "tep hop le, tren giay khong co gi".
    if (!annotation.imageRef) {
      return reject(
        'unsupported-feature',
        'Ảnh chèn vào chưa có dữ liệu ảnh. Hãy chọn lại tệp ảnh rồi chèn lần nữa.',
      );
    }
    if (width <= 0 || height <= 0) {
      return reject('unsupported-feature', 'Vùng đặt ảnh rỗng — sẽ không thấy gì trên trang.');
    }
  }

  if (!finite(annotation.opacity) || annotation.opacity > 1) {
    return reject('unsupported-feature', 'Độ mờ của chú thích phải nằm trong khoảng 0–1.');
  }
  if (annotation.opacity < ANNOTATION_MIN_OPACITY) {
    return reject(
      'unsupported-feature',
      `Độ mờ ${annotation.opacity} khiến dấu này VÔ HÌNH trên trang. Tệp vẫn hợp lệ và ` +
        'trình xem vẫn liệt kê chú thích, nhưng người dùng sẽ không thấy gì cả.',
    );
  }

  const color = annotation.colorRgb;
  if (!finite(color[0], color[1], color[2])) {
    return reject('unsupported-feature', 'Màu của chú thích không hợp lệ.');
  }
  if (color.some((c) => c < 0 || c > 1)) {
    return reject('unsupported-feature', 'Màu của chú thích phải nằm trong khoảng 0–1 mỗi kênh.');
  }
  // ANH duoc mien cua chan mau: `colorRgb` cua mot /Stamp khong ve gi ca - chinh
  // anh mang mau cua no. Chan mot buc anh vi "mau ghi trong chu thich qua nhat"
  // se la tu choi mot thao tac hoan toan hop le voi mot ly do vo nghia.
  const distance = colorDistance(color, WHITE);
  if (annotation.kind !== 'stamp' && distance < ANNOTATION_MIN_COLOR_DISTANCE) {
    return reject(
      'unsupported-feature',
      `Màu này quá gần màu trắng (khoảng cách ${distance.toFixed(3)} < ` +
        `${ANNOTATION_MIN_COLOR_DISTANCE}). Dấu ghi ra sẽ VÔ HÌNH trên nền trang: tệp vẫn hợp lệ, ` +
        'trình xem vẫn đọc được chú thích, nhưng trên giấy không có gì. Hãy chọn màu đậm hơn.',
    );
  }

  if (annotation.kind === 'ink') {
    const paths = annotation.inkPaths ?? [];
    const usable = paths.filter((p) => p.length >= 2);
    if (usable.length === 0) {
      return reject(
        'unsupported-feature',
        'Nét vẽ tay cần ít nhất hai điểm. Một nét rỗng sẽ ghi ra một chú thích không vẽ gì.',
      );
    }
    for (const path of usable) {
      for (const point of path) {
        if (!finite(point.x, point.y)) {
          return reject('unsupported-feature', 'Nét vẽ tay chứa tọa độ không hợp lệ.');
        }
      }
    }
  }

  // 'ink', 'text-note' va 'free-text' KHONG bi bat buoc co kich thuoc tu giao
  // dien: hop bao cua chung do duoc TU noi dung o buoc ghi (hop bao net ve, hoac
  // phep do hop chu). Bat mot kich thuoc o day se tu choi mot hop chu hop le chi
  // vi giao dien moi chi biet diem nguoi dung bam.
  if (annotation.kind !== 'ink' && annotation.kind !== 'text-note' && annotation.kind !== 'free-text') {
    if (width === 0 && height === 0) {
      return reject('unsupported-feature', 'Vùng của chú thích rỗng — sẽ không thấy gì trên trang.');
    }
  }

  return OK;
}

/**
 * CANH BAO TRUNG THUC ve chu thich, hien MOT LAN cho moi tai lieu.
 *
 * Dat canh PAGE_OPS_FIDELITY_WARNING trong pages/plan.ts va vi cung mot ly do:
 * day la su that ve DUONG GHI, khong phai ve mot man hinh cu the.
 */
export const ANNOTATION_FIDELITY_NOTE =
  'Chú thích được ghi thành đối tượng /Annot thật trong tệp, kèm dòng hình dạng (/AP), ' +
  'nên Acrobat và các trình xem khác vẫn mở ra và sửa lại được. Chú thích KHÔNG che nội dung ' +
  'bên dưới: chữ gốc vẫn nằm nguyên trong tệp và vẫn sao chép ra được. Đây không phải che thông tin.';
